import { NavLink } from 'react-router-dom'
import dayjs from 'dayjs'
import { FilterMatchMode } from 'primereact/api'
import { Calendar } from 'primereact/calendar'
import { ButtonGroup, Dropdown, DropdownButton } from 'react-bootstrap'
import { useState } from 'react'
import DeleteDialog from '../../../../common/dialogs/DeleteDialog'
import PageHeader from '../../../../layout/page/PageHeader'
import DataTable from '../../../../common/data-table/DataTable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faBan,
    faCheckCircle,
    faEdit,
    faEnvelope,
    faMinusCircle,
} from '@fortawesome/free-solid-svg-icons'
import { useToast } from '../../../../common/toast/hooks/useToast'
import SendEmailDialog from './SendEmailDialog'

const Requests = () => {
    const { triggerToast } = useToast()
    const [refreshTable, setRefreshTable] = useState(false)
    const [showSendEmail, setShowSendEmail] = useState(false)

    const dateTimeBodyTemplate = (rowData, field) => {
        return rowData[field]
            ? dayjs(rowData[field]).format('DD-MMM-YYYY @ HH:mm:ss')
            : ''
    }

    const dateFilterTemplate = (options) => {
        return (
            <Calendar
                value={options.value}
                onChange={(e) => options.filterCallback(e.value, options.index)}
                dateFormat="dd/mm/yy"
                placeholder="dd/mm/yyyy"
                mask="99/99/9999"
            />
        )
    }

    const actionsBodyTemplate = (rowData) => {
        return (
            <ButtonGroup size="sm">
                <DeleteDialog
                    url={'/subscriptions/delete/' + rowData.id}
                    record={rowData}
                    deletedCallback={() => setRefreshTable(true)}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete subscription for{' '}
                            <strong>{rowData.email}</strong>?
                        </p>
                    }
                />
                <NavLink
                    to={'/people/clients/subscription-requests/view/' + rowData.id}
                    className="btn btn-outline-primary ms-1"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </NavLink>
            </ButtonGroup>
        )
    }

    const statusTemplate = (rowData) => {
        switch (rowData.status) {
            case 'active':
                return (
                    <span className="text-success">
                        <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="me-2"
                        />
                        Active
                    </span>
                )
            case 'disabled':
                return (
                    <span className="text-warning">
                        <FontAwesomeIcon
                            icon={faMinusCircle}
                            className="me-2"
                        />
                        Disabled
                    </span>
                )
            case 'banned':
                return (
                    <span className="text-danger">
                        <FontAwesomeIcon icon={faBan} className="me-2" />
                        Banned
                    </span>
                )
            default:
                return rowData.status
        }
    }

    return (
        <>
            <PageHeader
                icon="users"
                subtitle="View and manage email subscriptions"
                gradient="linear-gradient(135deg, #8b5cf6, #7c3aed)"
                header="Subscriptions"
                buttons={[
                    {
                        variant: 'info',
                        label: 'Send Email',
                        icon: 'envelope',
                        onClick: () => setShowSendEmail(true),
                    },
                ]}
            />
            <DataTable
                refreshTable={refreshTable}
                toolbarConfig={{
                    includeExportBtn: true,
                }}
                config={{
                    dataRoute: '/subscriptions',
                    first: 0,
                    rows: 10,
                    page: 0,
                    sortField: 'created_at',
                    sortOrder: -1,
                    filters: {
                        global: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        email: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        status: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        source: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        created_at: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        updated_at: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                    },
                    filterDisplay: 'menu',
                }}
                columns={[
                    {
                        field: 'email',
                        header: 'Email',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                    },
                    {
                        field: 'status',
                        header: 'Status',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        body: statusTemplate,
                        headerStyle: {
                            maxWidth: '2rem',
                        },
                        style: {
                            maxWidth: '2rem',
                        },
                    },
                    {
                        field: 'source',
                        header: 'Source',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        headerStyle: {
                            maxWidth: '2rem',
                        },
                        style: {
                            maxWidth: '2rem',
                        },
                    },
                    {
                        field: 'created_at',
                        header: 'Subscribed At',
                        sortable: true,
                        filter: true,
                        dataType: 'date',
                        body: (rowData) =>
                            dateTimeBodyTemplate(rowData, 'created_at'),
                        filterElement: dateFilterTemplate,
                        style: {
                            maxWidth: '3rem',
                        },
                    },
                    {
                        field: 'updated_at',
                        header: 'Updated At',
                        sortable: true,
                        filter: true,
                        dataType: 'date',
                        body: (rowData) =>
                            dateTimeBodyTemplate(rowData, 'updated_at'),
                        filterElement: dateFilterTemplate,
                        style: {
                            maxWidth: '3rem',
                        },
                        isVisible: false,
                    },
                    {
                        field: 'id',
                        header: 'Actions',
                        sortable: false,
                        body: actionsBodyTemplate,
                        alignHeader: 'center',
                        headerStyle: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        style: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        toggleVisibility: false,
                    },
                ]}
                globalFilterFields={['email', 'status', 'source']}
            />
            <SendEmailDialog
                show={showSendEmail}
                onHide={() => setShowSendEmail(false)}
                onSent={() => {
                    setShowSendEmail(false)
                    triggerToast({
                        severity: 'success',
                        summary: 'Success!',
                        detail: 'Email sent successfully!',
                        life: 5000,
                    })
                }}
            />
        </>
    )
}

export default Requests
